import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Save, CheckCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { errorText } from '../lib/errorText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Elige qué stickers puede usar Noah en una campaña concreta. */
export const CampaignStickersDialog = ({ campaign, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!campaign) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/${campaign.id}/stickers`);
      setData(res.data);
      setSel(new Set(res.data.allowed || []));
    } catch (e) {
      toast.error(errorText(e, 'No se pudieron cargar los stickers'));
    } finally {
      setLoading(false);
    }
  }, [campaign]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (key) => {
    const next = new Set(sel);
    next.has(key) ? next.delete(key) : next.add(key);
    setSel(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const everything = sel.size === all.length && all.length > 0;
      await axios.put(`${API}/campaigns/${campaign.id}/stickers`, { allowed: everything ? null : [...sel] });
      toast.success(everything
        ? `Noah podrá usar todos los stickers en ${campaign.name}`
        : `Noah usará ${sel.size} stickers en ${campaign.name}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(errorText(e, 'No se pudo guardar'));
    } finally {
      setSaving(false);
    }
  };

  const all = [
    ...(data?.catalog || []).map((c) => ({ key: c.key, label: c.text, src: c.image })),
    ...(data?.custom || []).map((c) => ({ key: c.key, label: c.text, src: `${process.env.REACT_APP_BACKEND_URL}${c.image}` })),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="campaign-stickers-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Stickers de Noah · {campaign?.name}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-xs text-muted-foreground flex-1 min-w-[180px]">
                Marca los que Noah puede usar en esta campaña. Los grises no los usará nunca.
              </p>
              <Button variant="ghost" size="sm" onClick={() => setSel(new Set(all.map((a) => a.key)))}
                data-testid="stickers-select-all" className="gap-1 text-xs">
                <CheckCheck className="w-3.5 h-3.5" /> Todos
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSel(new Set())}
                data-testid="stickers-select-none" className="gap-1 text-xs">
                <X className="w-3.5 h-3.5" /> Ninguno
              </Button>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {all.map((s) => {
                const active = sel.has(s.key);
                return (
                  <button key={s.key} onClick={() => toggle(s.key)}
                    data-testid={`campaign-sticker-${s.key}`}
                    title={s.label}
                    className={`p-1.5 rounded-2xl border transition-all ${
                      active ? 'border-primary bg-primary/10' : 'border-border bg-muted/20 opacity-40 grayscale'
                    }`}>
                    <img src={s.src} alt={s.label} className="w-full h-auto" loading="lazy" />
                  </button>
                );
              })}
            </div>
          </>
        )}

        <DialogFooter>
          <span className="text-xs text-muted-foreground sm:mr-auto self-center">{sel.size} seleccionados</span>
          <Button onClick={save} disabled={saving} data-testid="campaign-stickers-save" className="gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignStickersDialog;
